"use client";

import { useEffect } from "react";
import { useQuery } from "convex/react";

import type { EventMetadata } from "@soonlist/cal";
import type { AddToCalendarButtonPropsRestricted } from "@soonlist/cal/types";
import { api } from "@soonlist/backend/convex/_generated/api";
import { Skeleton } from "@soonlist/ui/skeleton";

import { EventPage } from "~/components/EventDisplays";
import { OpenInAppBanner } from "~/components/OpenInAppBanner";
import { transformConvexUser } from "~/lib/transformConvexUser";
import { createOneLink } from "~/lib/urlScheme";

export default function EventPageClient({ eventId }: { eventId: string }) {
  const event = useQuery(api.events.get, { eventId });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [eventId]);

  const appUrl = createOneLink({
    deepLinkValue: "event",
    deepLinkSub1: eventId,
    campaign: "open_in_app",
  });

  if (event === undefined) {
    return (
      <div className="flex flex-col gap-4">
        <OpenInAppBanner appUrl={appUrl} />
        <div className="relative left-1/2 right-1/2 -mb-16 -ml-[50vw] -mr-[50vw] -mt-6 min-h-screen w-screen bg-interactive-3 px-4 pb-8 pt-4 sm:px-6 lg:-my-24 lg:pb-12 lg:pt-6">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
            <div className="flex justify-end gap-2">
              <Skeleton className="h-9 w-24 rounded-full" />
              <Skeleton className="h-9 w-9 rounded-full" />
            </div>
            <div className="flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm sm:p-6">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-9 w-3/4" />
              <Skeleton className="h-5 w-2/3" />
              <Skeleton className="h-32 w-full rounded-[28px]" />
              <Skeleton className="mx-auto h-5 w-28" />
            </div>
            <div className="rounded-3xl bg-white p-4 shadow-sm">
              <Skeleton className="aspect-[9/16] w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <p className="text-lg text-gray-500">No event found for ID: {eventId}</p>
    );
  }

  const calendarData = event.event as AddToCalendarButtonPropsRestricted;
  const metadata = event.eventMetadata as EventMetadata;

  const fullImageUrl = calendarData.images?.[3] || null;

  const user = event.user ? transformConvexUser(event.user) : undefined;
  const createdAt = new Date(event._creationTime);

  return (
    <>
      <OpenInAppBanner appUrl={appUrl} />
      <div className="relative left-1/2 right-1/2 -mb-16 -ml-[50vw] -mr-[50vw] -mt-6 min-h-screen w-screen bg-interactive-3 px-4 pb-8 pt-4 sm:px-6 lg:-my-24 lg:pb-12 lg:pt-6">
        <EventPage
          user={user}
          eventFollows={event.eventFollows ?? []}
          comments={event.comments ?? []}
          key={event.id}
          id={event.id}
          event={calendarData}
          eventMetadata={metadata}
          createdAt={createdAt}
          visibility={event.visibility}
          archivedAt={event.archivedAt ?? null}
          similarEvents={undefined}
          image={fullImageUrl}
          singleEvent
          hideCurator
          lists={event.lists ?? []}
        />
      </div>
    </>
  );
}
